<template>
  <div>
    <div id="page-panel">
      <div class="customer-top-nav">
        <div class="title">
          <span>Importation privee - lots inventaire IP</span><br>
        </div>
        <div class="controls no-print">
          <el-button
            size="small"
            icon="el-icon-s-operation"
            @click="filtersDrawerVisible = true">
            Filtres
          </el-button>
          <el-button
            type="primary"
            plain
            size="small"
            icon="el-icon-download"
            @click="downloadExcel"
            :disabled="loading || filteredRows.length === 0"
          />
        </div>
      </div>

      <p>
        <span class="fs-14 secondary-text">{{ companyName }}</span>
      </p>

      <div class="filters-row">
        <el-radio-group v-model="showAllLots" size="small" @change="fetchData">
          <el-radio-button :label="false">Lots avec quantite</el-radio-button>
          <el-radio-button :label="true">Tous les lots</el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-model="selectedDate"
          type="date"
          placeholder="Date du"
          size="small"
          :disabled="!showAllLots"
          @change="fetchData"
        />
        <el-input
          v-model="searchTerm"
          size="small"
          clearable
          placeholder="Recherche intelligente produit / producteur"
          @input="applyFilters"
          @clear="applyFilters"
        />
      </div>

      <p class="secondary-text fs-14 mb-10">
        Lots affiches: <strong>{{ filteredRows.length }}</strong>
      </p>

      <el-table
        v-loading="loading"
        :data="filteredRows"
        :cell-class-name="getCellClassName"
        border
        stripe
        style="width: 100%"
        height="650"
        empty-text="Aucun lot trouve pour cette plage de dates"
      >
        <el-table-column prop="productName" label="Produit" min-width="300" sortable fixed="left">
          <template slot-scope="scope">
            <el-link
              v-if="scope.row.puid"
              type="primary"
              :underline="false"
              @click="openProductDrawer(scope.row.puid)">
              {{ scope.row.productName }}
            </el-link>
            <span v-else>{{ scope.row.productName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="vintage" label="Millesime" width="120" sortable />
        <el-table-column prop="providerName" label="Producteur" min-width="220" sortable>
          <template slot-scope="scope">
            <PortausLink
              v-if="scope.row.providerId"
              :id="scope.row.providerId"
              path="/customers/"
              :label="scope.row.providerName"
              :openNewTab="true"
            />
            <span v-else>{{ scope.row.providerName }}</span>
          </template>
        </el-table-column>
          <el-table-column prop="price" label="Prix licencié" width="120" align="right" sortable>
            <template slot-scope="scope">
              {{ formatPrice(scope.row.price) }}
            </template>
          </el-table-column>
        <el-table-column prop="agencyFee" label="Frais d'agence" width="120" align="right" sortable :sort-method="sortByAgencyFee">
          <template slot-scope="scope">
            {{ formatPrice(scope.row.agencyFee) }}
          </template>
        </el-table-column>
        <el-table-column prop="reference" label="No reference lot" min-width="170" sortable />
        <el-table-column prop="arrivalDateDisplay" label="Date d'arrivage" width="140" sortable :sort-method="sortByArrivalDate" />
        <el-table-column prop="originalQty" label="Qté origine" width="120" align="right" sortable>
          <template slot-scope="scope">
            {{ formatQty(scope.row.originalQty) }}
          </template>
        </el-table-column>
        <el-table-column prop="remainingQty" label="Qté restante" width="120" align="right" sortable>
          <template slot-scope="scope">
            <strong>{{ formatQty(scope.row.remainingQty) }}</strong>
          </template>
        </el-table-column>
        <el-table-column prop="daysSinceArrival" label="Nb jours depuis arrivage" width="190" align="right" sortable />
        <el-table-column prop="plus150DateDisplay" label="Date +150 jours" width="140" sortable :sort-method="sortByPlus150Date" />
        <el-table-column prop="plus180DateDisplay" label="Date +180 jours" width="140" sortable :sort-method="sortByPlus180Date" />
        <el-table-column prop="plus210DateDisplay" label="Date +210 jours" width="140" sortable :sort-method="sortByPlus210Date" />
        <el-table-column prop="plus240DateDisplay" label="Date +240 jours" width="140" sortable :sort-method="sortByPlus240Date" />
        <el-table-column prop="plus270DateDisplay" label="Date +270 jours" width="140" sortable :sort-method="sortByPlus270Date" />
        <el-table-column prop="plus300DateDisplay" label="Date +300 jours" width="140" sortable :sort-method="sortByPlus300Date" />
        <el-table-column prop="plus330DateDisplay" label="Date +330 jours" width="140" sortable :sort-method="sortByPlus330Date" />
        <el-table-column prop="plus360DateDisplay" label="Date +360 jours" width="140" sortable :sort-method="sortByPlus360Date" />
      </el-table>

      <ProductDetailDrawer
        :puid="selectedPuid"
        direction="ltr"
        :visible="productDetailDrawerVisible"
        @close="closeProductDetailDrawer"
      />

      <el-drawer
        title="Filtres"
        :visible.sync="filtersDrawerVisible"
        direction="rtl"
        size="420px"
        :append-to-body="true">
        <div class="filters-drawer-content">
          <el-select
            v-model="selectedProductFilter"
            size="small"
            clearable
            filterable
            placeholder="Filtrer par produit"
            @change="applyFilters"
          >
            <el-option
              v-for="product in productFilterOptions"
              :key="product"
              :label="product"
              :value="product"
            />
          </el-select>

          <el-select
            v-model="selectedProviderFilter"
            size="small"
            clearable
            filterable
            placeholder="Filtrer par producteur"
            @change="applyFilters"
          >
            <el-option
              v-for="provider in providerFilterOptions"
              :key="provider"
              :label="provider"
              :value="provider"
            />
          </el-select>

          <div class="filters-drawer-actions">
            <el-button size="small" @click="resetFilters">Reinitialiser</el-button>
            <el-button type="primary" size="small" @click="closeFiltersDrawer">Appliquer</el-button>
          </div>
        </div>
      </el-drawer>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import Fuse from 'fuse.js/dist/fuse.common';
import XLSX from 'xlsx';
import { saveAs } from 'file-saver';

import InventoryAPI from '@/services/API/inventory';
import PortausLink from '@/components/portaus-link';
import ProductDetailDrawer from '@/views/pages/inventory/components/product-detail-drawer';

export default {
  name: 'PrivateImportLotsIpReport',
  components: {
    PortausLink,
    ProductDetailDrawer,
  },
  props: {
    width: {
      type: Number,
      default: 100,
    },
    companyName: '',
  },
  data() {
    const selectedDate = moment().subtract(1, 'year').startOf('day');

    return {
      loading: false,
      rows: [],
      filteredRows: [],
      selectedDate: selectedDate.toDate(),
      showAllLots: false,
      searchTerm: '',
      selectedProductFilter: null,
      selectedProviderFilter: null,
      filtersDrawerVisible: false,
      selectedPuid: null,
      productDetailDrawerVisible: false,
    };
  },
  computed: {
    productFilterOptions() {
      return _.chain(this.rows)
        .map(row => _.trim(_.get(row, 'productName', '')))
        .filter(name => name !== '')
        .uniq()
        .sortBy()
        .value();
    },
    providerFilterOptions() {
      return _.chain(this.rows)
        .map(row => _.trim(_.get(row, 'providerName', '')))
        .filter(name => name !== '')
        .uniq()
        .sortBy()
        .value();
    },
  },
  methods: {
    formatQty(value) {
      return Math.round(Number(value || 0));
    },
    formatPrice(value) {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        return '';
      }
      return `${numericValue.toFixed(2)}$`;
    },
    sortByDateValue(field) {
      return (a, b) => Number(_.get(a, field, 0)) - Number(_.get(b, field, 0));
    },
    sortByAgencyFee(a, b) {
      return Number(_.get(a, 'agencyFee', 0)) - Number(_.get(b, 'agencyFee', 0));
    },
    sortByArrivalDate(a, b) {
      return this.sortByDateValue('arrivalDateValue')(a, b);
    },
    sortByPlus150Date(a, b) {
      return this.sortByDateValue('plus150DateValue')(a, b);
    },
    sortByPlus180Date(a, b) {
      return this.sortByDateValue('plus180DateValue')(a, b);
    },
    sortByPlus210Date(a, b) {
      return this.sortByDateValue('plus210DateValue')(a, b);
    },
    sortByPlus240Date(a, b) {
      return this.sortByDateValue('plus240DateValue')(a, b);
    },
    sortByPlus270Date(a, b) {
      return this.sortByDateValue('plus270DateValue')(a, b);
    },
    sortByPlus300Date(a, b) {
      return this.sortByDateValue('plus300DateValue')(a, b);
    },
    sortByPlus330Date(a, b) {
      return this.sortByDateValue('plus330DateValue')(a, b);
    },
    sortByPlus360Date(a, b) {
      return this.sortByDateValue('plus360DateValue')(a, b);
    },
    isDateExceeded(dateValue, row) {
      const remainingQty = Number(_.get(row, 'remainingQty', 0));
      if (remainingQty <= 0) {
        return false;
      }

      const normalizedDateValue = Number(dateValue || 0);
      if (normalizedDateValue <= 0) {
        return false;
      }

      const todayStart = moment().startOf('day').valueOf();
      return normalizedDateValue < todayStart;
    },
    hasAnyExceededDate(row) {
      const dateValueFields = [
        'plus150DateValue',
        'plus180DateValue',
        'plus210DateValue',
        'plus240DateValue',
        'plus270DateValue',
        'plus300DateValue',
        'plus330DateValue',
        'plus360DateValue',
      ];

      return _.some(dateValueFields, field => this.isDateExceeded(_.get(row, field, 0), row));
    },
    getCellClassName({ row, column }) {
      const columnProperty = _.get(column, 'property', '');
      if (columnProperty === 'productName') {
        return this.hasAnyExceededDate(row) ? 'lot-date-overdue' : '';
      }

      if (!/^plus\d+DateDisplay$/.test(columnProperty)) {
        return '';
      }

      const valueProperty = columnProperty.replace('Display', 'Value');
      return this.isDateExceeded(_.get(row, valueProperty, 0), row) ? 'lot-date-overdue' : '';
    },
    openProductDrawer(puid) {
      if (!puid) {
        return;
      }
      this.selectedPuid = puid;
      this.productDetailDrawerVisible = true;
    },
    closeProductDetailDrawer() {
      this.selectedPuid = null;
      this.productDetailDrawerVisible = false;
    },
    closeFiltersDrawer() {
      this.filtersDrawerVisible = false;
    },
    resetFilters() {
      this.selectedProductFilter = null;
      this.selectedProviderFilter = null;
      this.applyFilters();
    },
    getSelectedDate() {
      if (!this.selectedDate) {
        return null;
      }
      const selectedDate = moment(this.selectedDate);
      return selectedDate.isValid() ? selectedDate.format('YYYY-MM-DD') : null;
    },
    downloadExcel() {
      if (this.filteredRows.length === 0) {
        this.$message.warning('Aucune donnee a exporter');
        return;
      }

      const rows = this.filteredRows.map(row => ({
        Produit: row.productName,
        Millesime: row.vintage,
        Producteur: row.providerName,
        'Prix licencié': Number.isFinite(Number(row.price)) ? Number(row.price) : '',
        "Frais d'agence": Number.isFinite(Number(row.agencyFee)) ? Number(row.agencyFee) : '',
        'No reference lot': row.reference,
        "Date d'arrivage": row.arrivalDateDisplay,
        'Qte origine': this.formatQty(row.originalQty),
        'Qte restante': this.formatQty(row.remainingQty),
        'Nb jours depuis arrivage': row.daysSinceArrival,
        'Date +150 jours': row.plus150DateDisplay,
        'Date +180 jours': row.plus180DateDisplay,
        'Date +210 jours': row.plus210DateDisplay,
        'Date +240 jours': row.plus240DateDisplay,
        'Date +270 jours': row.plus270DateDisplay,
        'Date +300 jours': row.plus300DateDisplay,
        'Date +330 jours': row.plus330DateDisplay,
        'Date +360 jours': row.plus360DateDisplay,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Lots IP');
      const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
      const filename = `lots-ip-${moment().format('YYYY-MM-DD')}.xlsx`;
      saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
    },
    applyFilters() {
      let rows = _.filter(this.rows, row => row.arrivalDateMoment && row.arrivalDateMoment.isValid());

      if (this.selectedProductFilter) {
        rows = _.filter(rows, row => _.get(row, 'productName', '') === this.selectedProductFilter);
      }

      if (this.selectedProviderFilter) {
        rows = _.filter(rows, row => _.get(row, 'providerName', '') === this.selectedProviderFilter);
      }

      const query = _.trim(this.searchTerm || '');
      if (query !== '') {
        const fuse = new Fuse(rows, {
          includeScore: true,
          threshold: 0.35,
          ignoreLocation: true,
          keys: [
            { name: 'productName', weight: 0.7 },
            { name: 'providerName', weight: 0.3 },
          ],
        });
        rows = _.map(fuse.search(query), 'item');
      }

      // Default grouping/sorting: product asc, then lot arrival date desc.
      rows = _.orderBy(rows, ['productName', 'arrivalDateValue', 'reference'], ['asc', 'desc', 'asc']);
      this.filteredRows = rows;
    },
    fetchData() {
      this.loading = true;
      const selectedDate = this.getSelectedDate();

      return InventoryAPI.fetchPrivateImportLotsIpReport({
        selectedDate: this.showAllLots && selectedDate != null ? selectedDate : undefined,
        showAllLots: this.showAllLots,
      })
        .then((response) => {
          const rows = _.get(response, 'rows', []);
          this.rows = _.map(rows, (row) => {
            const arrivalDateMoment = moment(row.availabilityDate);
            const plus150DateMoment = moment(row.plus150Date);
            const plus180DateMoment = moment(row.plus180Date);
            const plus210DateMoment = moment(row.plus210Date);
            const plus240DateMoment = moment(row.plus240Date);
            const plus270DateMoment = moment(row.plus270Date);
            const plus300DateMoment = moment(row.plus300Date);
            const plus330DateMoment = moment(row.plus330Date);
            const plus360DateMoment = moment(row.plus360Date);

            return {
              puid: row.puid || null,
              productName: row.productName || '',
              vintage: row.vintage || '',
              price: _.get(row, 'price', null),
              agencyFee: _.get(row, 'productInventory.pricing.agencyFee', null),
              providerId: row.providerId != null ? Number(row.providerId) : null,
              providerName: row.providerName || '',
              reference: row.reference || '',
              originalQty: Number(row.originalQty || 0),
              remainingQty: Number(row.remainingQty || 0),
              daysSinceArrival: Number(row.daysSinceArrival || 0),
              arrivalDateMoment,
              arrivalDateValue: arrivalDateMoment.isValid() ? arrivalDateMoment.valueOf() : 0,
              arrivalDateDisplay: arrivalDateMoment.isValid() ? arrivalDateMoment.format('YYYY-MM-DD') : '',
              plus150DateValue: plus150DateMoment.isValid() ? plus150DateMoment.valueOf() : 0,
              plus150DateDisplay: plus150DateMoment.isValid() ? plus150DateMoment.format('YYYY-MM-DD') : '',
              plus180DateValue: plus180DateMoment.isValid() ? plus180DateMoment.valueOf() : 0,
              plus180DateDisplay: plus180DateMoment.isValid() ? plus180DateMoment.format('YYYY-MM-DD') : '',
              plus210DateValue: plus210DateMoment.isValid() ? plus210DateMoment.valueOf() : 0,
              plus210DateDisplay: plus210DateMoment.isValid() ? plus210DateMoment.format('YYYY-MM-DD') : '',
              plus240DateValue: plus240DateMoment.isValid() ? plus240DateMoment.valueOf() : 0,
              plus240DateDisplay: plus240DateMoment.isValid() ? plus240DateMoment.format('YYYY-MM-DD') : '',
              plus270DateValue: plus270DateMoment.isValid() ? plus270DateMoment.valueOf() : 0,
              plus270DateDisplay: plus270DateMoment.isValid() ? plus270DateMoment.format('YYYY-MM-DD') : '',
              plus300DateValue: plus300DateMoment.isValid() ? plus300DateMoment.valueOf() : 0,
              plus300DateDisplay: plus300DateMoment.isValid() ? plus300DateMoment.format('YYYY-MM-DD') : '',
              plus330DateValue: plus330DateMoment.isValid() ? plus330DateMoment.valueOf() : 0,
              plus330DateDisplay: plus330DateMoment.isValid() ? plus330DateMoment.format('YYYY-MM-DD') : '',
              plus360DateValue: plus360DateMoment.isValid() ? plus360DateMoment.valueOf() : 0,
              plus360DateDisplay: plus360DateMoment.isValid() ? plus360DateMoment.format('YYYY-MM-DD') : '',
            };
          });
          this.applyFilters();
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.rows = [];
          this.filteredRows = [];
          this.$message.error('Une erreur systeme est survenue. Veuillez reessayer plus tard');
        });
    },
  },
  created() {
    this.fetchData();
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../assets/scss/_variables';

.title {
  font-size: 1.6em;
  font-weight: bold;
  margin-right: 20px;
  position: relative;
  float: left;
}

.customer-top-nav {
  height: 40px;
}

.controls {
  position: relative;
  float: right;
  display: flex;
  align-items: center;

  button {
    margin-left: 10px;
  }
}

.filters-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 12px;

  .el-input,
  .el-select {
    min-width: 240px;
  }
}

.filters-drawer-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  .el-select {
    width: 100%;
  }
}

.filters-drawer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

::v-deep .el-table .lot-date-overdue {
  background-color: #fdeaea !important;
}
</style>
